import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const P = "monetization.payment";
const MAX_POLLS = 8;

export default function PaymentResult() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const isCancel = location.pathname.includes("/cancel");
  const [state, setState] = useState(isCancel ? "cancel" : "processing"); // processing | paid | fail | cancel
  const [kind, setKind] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (isCancel) return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("fail"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.kind) setKind(data.kind);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
      } catch { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("fail"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, params, refresh]);

  const card = (icon, title, desc, actions) => (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 bg-gradient-to-br from-[#F8FAF9] to-teal-50">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center cm-fade-up" data-testid="payment-result">
        {icon}
        <h1 className="font-display font-bold text-2xl mt-4">{title}</h1>
        <p className="text-sm text-slate-500 mt-2">{desc}</p>
        <div className="mt-6 space-y-2">{actions}</div>
      </div>
    </div>
  );

  if (state === "processing")
    return card(
      <Loader2 className="h-12 w-12 mx-auto text-[#008080] animate-spin" data-testid="payment-processing" />,
      t(`${P}.processing`), "", null);

  if (state === "paid") {
    const isBoost = kind === "boost";
    const isSale = kind === "sale";
    if (isSale)
      return card(
        <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-9 w-9" /></span>,
        t("purchase.success"), "",
        <Button data-testid="payment-go-messages" onClick={() => navigate("/messages")} className="w-full bg-[#008080] hover:bg-[#006666]">{t("nav.messages")}</Button>);
    return card(
      <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-9 w-9" /></span>,
      isBoost ? t("sell.boostedPublished") : t(`${P}.successTitle`),
      isBoost ? "" : t(`${P}.successDesc`),
      isBoost
        ? <Button data-testid="payment-go-dashboard" onClick={() => navigate("/dashboard")} className="w-full bg-[#008080] hover:bg-[#006666]">{t("nav.dashboard")}</Button>
        : <Button data-testid="payment-go-dashboard" onClick={() => navigate("/dashboard")} className="w-full bg-[#008080] hover:bg-[#006666]">{t("nav.dashboard")}</Button>);
  }

  if (state === "cancel")
    return card(
      <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-slate-100 text-slate-500"><XCircle className="h-9 w-9" /></span>,
      t(`${P}.cancelTitle`), t(`${P}.cancelDesc`),
      <Button data-testid="payment-retry" onClick={() => navigate("/profile")} variant="outline" className="w-full">{t(`${P}.retry`)}</Button>);

  return card(
    <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-amber-50 text-amber-600"><AlertTriangle className="h-9 w-9" /></span>,
    t(`${P}.failTitle`), t(`${P}.failDesc`),
    <>
      <Button data-testid="payment-reload" onClick={() => window.location.reload()} className="w-full bg-[#008080] hover:bg-[#006666]">{t(`${P}.retry`)}</Button>
      <Button data-testid="payment-go-profile" onClick={() => navigate("/profile")} variant="outline" className="w-full">{t(`${P}.goProfile`)}</Button>
    </>);
}
